import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, Save, Download, AlertTriangle, CloudHail, Wind, Tornado, MapPin, Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StormMapView } from './StormMapView';
import type { StormReport } from '@/hooks/useStormReports';
import { useNavigate } from 'react-router-dom';

interface AddressSummary {
  address: string;
  latitude: number;
  longitude: number;
  radius_miles: number;
  days_searched: number;
  total_events: number;
  hail_count: number;
  wind_count: number;
  tornado_count: number;
  max_hail_inches: number;
  max_wind_mph: number;
  last_storm_date: string | null;
  damage_likely: boolean;
}

export function AddressReportTab() {
  const { user, orgId } = useAuthContext();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [radius, setRadius] = useState(10);
  const [days, setDays] = useState(365);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<AddressSummary | null>(null);
  const [events, setEvents] = useState<(StormReport & { distance_miles?: number })[]>([]);

  const runReport = async () => {
    if (!address.trim()) return;
    setLoading(true);
    setSummary(null);
    setEvents([]);
    try {
      const { data, error } = await supabase.functions.invoke('address-storm-history', {
        body: { address, radiusMiles: radius, days },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setSummary(data.summary);
      setEvents(data.events || []);
    } catch (e: any) {
      toast.error(e.message || 'Failed to run storm report');
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    if (!summary || !orgId) return;
    const { error } = await supabase.from('storm_address_reports').insert({
      org_id: orgId,
      created_by: user?.id,
      address: summary.address,
      latitude: summary.latitude,
      longitude: summary.longitude,
      report_data: { summary, events: events.slice(0, 100) } as any,
      max_hail_inches: summary.max_hail_inches,
      max_wind_mph: summary.max_wind_mph,
      storm_count: summary.total_events,
      last_storm_date: summary.last_storm_date,
    });
    if (error) toast.error(error.message);
    else toast.success('Report saved');
  };

  const createLeadFromReport = async () => {
    if (!summary || !orgId) return;
    const { data, error } = await supabase.from('leads').insert({
      org_id: orgId,
      created_by: user?.id,
      contact_name: 'Storm Lead — ' + summary.address.split(',')[0],
      property_address: summary.address,
      source: 'storm_watch',
      lead_source: 'Storm Watch',
      lead_temperature: summary.damage_likely ? 'hot' : 'warm',
      status: 'new',
      notes: `Storm history: ${summary.total_events} events. Max hail ${summary.max_hail_inches.toFixed(2)}", max wind ${summary.max_wind_mph} mph. Last storm ${summary.last_storm_date}.`,
    } as any).select('id').single();
    if (error) toast.error(error.message);
    else {
      toast.success('Lead created');
      if (data) navigate(`/leads`);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1">
              <Input
                placeholder="Enter property address (e.g. 123 Main St, Dallas, TX)"
                value={address}
                onChange={e => setAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runReport()}
              />
            </div>
            <Select value={String(radius)} onValueChange={v => setRadius(Number(v))}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 mi radius</SelectItem>
                <SelectItem value="5">5 mi radius</SelectItem>
                <SelectItem value="10">10 mi radius</SelectItem>
                <SelectItem value="25">25 mi radius</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="90">Last 3 months</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
                <SelectItem value="730">Last 24 months</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={runReport} disabled={loading || !address.trim()} className="gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Run Report
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Searches NOAA Storm Prediction Center reports near the address. Larger time windows take longer.
          </p>
        </CardContent>
      </Card>

      {loading && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Pulling storm history... this can take 20-60s for long windows
        </CardContent></Card>
      )}

      {summary && (
        <>
          {/* Summary card */}
          <Card className={summary.damage_likely ? 'border-destructive/40 bg-destructive/5' : ''}>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-muted-foreground">PROPERTY</div>
                  <div className="font-semibold text-lg flex items-center gap-2"><MapPin className="w-4 h-4" /> {summary.address}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {summary.radius_miles} mi radius · {summary.days_searched} day lookback
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={saveReport} className="gap-1.5"><Save className="w-3.5 h-3.5" /> Save</Button>
                  <Button size="sm" onClick={createLeadFromReport} className="gap-1.5"><Download className="w-3.5 h-3.5" /> Create Lead</Button>
                </div>
              </div>

              {summary.damage_likely && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                  <span className="font-medium text-destructive">Damage likely</span>
                  <span className="text-muted-foreground">— hail ≥1" or wind ≥60 mph in this area</span>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <SummaryTile icon={<CloudHail className="w-4 h-4" />} label="Hail Events" value={summary.hail_count} sub={`Max ${summary.max_hail_inches.toFixed(2)}"`} color="text-orange-600 dark:text-orange-400" />
                <SummaryTile icon={<Wind className="w-4 h-4" />} label="Wind Events" value={summary.wind_count} sub={`Max ${summary.max_wind_mph} mph`} color="text-yellow-700 dark:text-yellow-400" />
                <SummaryTile icon={<Tornado className="w-4 h-4" />} label="Tornadoes" value={summary.tornado_count} color="text-purple-600 dark:text-purple-400" />
                <SummaryTile icon={<AlertTriangle className="w-4 h-4" />} label="Total Events" value={summary.total_events} color="text-red-600 dark:text-red-400" />
                <SummaryTile icon={<Calendar className="w-4 h-4" />} label="Last Storm" value={0} sub={summary.last_storm_date || 'None'} color="text-foreground" hideValue />
              </div>
            </CardContent>
          </Card>

          {/* Map of events */}
          <StormMapView reports={events} height="420px" />

          {/* Events list */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-border font-semibold text-sm">All Events ({events.length})</div>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Magnitude</th>
                      <th className="px-4 py-2">Distance</th>
                      <th className="px-4 py-2">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e, i) => (
                      <tr key={i} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-2 whitespace-nowrap">{e.date}</td>
                        <td className="px-4 py-2"><EventTypeBadge type={e.type} /></td>
                        <td className="px-4 py-2 font-medium">
                          {e.type === 'hail' ? `${e.magnitude.toFixed(2)}"` : e.type === 'wind' ? `${Math.round(e.magnitude)} mph` : `EF${Math.round(e.magnitude)}`}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{e.distance_miles?.toFixed(1)} mi</td>
                        <td className="px-4 py-2 text-muted-foreground">{e.location}, {e.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryTile({ icon, label, value, sub, color, hideValue }: { icon: React.ReactNode; label: string; value: number; sub?: string; color?: string; hideValue?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-3 bg-card">
      <div className={`flex items-center gap-1.5 text-xs font-medium ${color ?? 'text-muted-foreground'}`}>{icon}{label}</div>
      {!hideValue && <div className="text-2xl font-bold mt-1 text-foreground">{value}</div>}
      {sub && <div className={`text-xs mt-0.5 ${hideValue ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{sub}</div>}
    </div>
  );
}

function EventTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    hail: 'bg-orange-500/20 text-orange-700 dark:text-orange-400',
    wind: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
    tornado: 'bg-purple-500/20 text-purple-700 dark:text-purple-400',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[type]}`}>{type}</span>;
}
